import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { IndianRupee } from "lucide-react";
import api, { inr, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const FIELDS = [
  ["payee_name", "Payee name (shown to vendors)"],
  ["upi_id", "UPI ID (e.g. yourname@upi)"],
  ["account_name", "Bank account name"],
  ["account_number", "Bank account number"],
  ["ifsc", "IFSC"],
  ["bank_name", "Bank name"],
];

export default function AdminPayments() {
  const [cfg, setCfg] = useState({ payee_name: "", upi_id: "", account_name: "", account_number: "", ifsc: "", bank_name: "", note: "" });
  const [fees, setFees] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, f] = await Promise.all([api.get("/admin/payments/config"), api.get("/admin/payments/fees")]);
      setCfg(c.data);
      setFees(f.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put("/admin/payments/config", cfg);
      toast.success("Payment settings saved — vendors will see these when paying the platform fee");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const total = fees.reduce((s, f) => s + f.amount, 0);

  return (
    <div data-testid="admin-payments">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-slate-100 rounded-xl p-6">
          <p className="font-heading font-bold flex items-center gap-2"><IndianRupee className="w-4 h-4 text-blue-500" /> Vendor fee collection account</p>
          <p className="text-xs text-slate-400 mt-1 mb-5">Vendors see these details when paying the one-time platform admission fee.</p>
          <div className="space-y-3">
            {FIELDS.map(([k, label]) => (
              <label key={k} className="block text-xs font-semibold text-slate-500">{label}
                <input data-testid={`paycfg-${k}`} value={cfg[k] || ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
              </label>
            ))}
            <label className="block text-xs font-semibold text-slate-500">Note to vendors (optional)
              <input data-testid="paycfg-note" value={cfg.note || ""} onChange={(e) => setCfg({ ...cfg, note: e.target.value })}
                placeholder='e.g. "Share the UTR number on WhatsApp after paying"'
                className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
            </label>
          </div>
          <Button data-testid="paycfg-save" onClick={save} className="rounded-md bg-slate-900 mt-4">Save payment settings</Button>
        </div>

        <div className="border border-slate-100 rounded-xl overflow-hidden h-fit">
          <p className="px-5 py-3 bg-slate-50 text-xs font-bold uppercase tracking-widest text-slate-500 flex justify-between">
            <span>Platform fee transactions</span><span data-testid="fees-total">Total: {inr(total)}</span>
          </p>
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Vendor</TableHead><TableHead>Txn ID</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
            <TableBody>
              {fees.map((f) => (
                <TableRow key={f.id} data-testid={`fee-row-${f.id}`}>
                  <TableCell className="text-xs">{new Date(f.at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="font-semibold text-sm">{f.seller_name}</TableCell>
                  <TableCell className="font-mono text-xs">{f.txn_id}</TableCell>
                  <TableCell className="font-bold text-emerald-600">+{inr(f.amount)}</TableCell>
                </TableRow>
              ))}
              {!fees.length && <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400 text-sm">No platform fees collected yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
